import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { suggestionService } from '../../services/suggestionService';
import { Suggestion } from '../../types/crossSystem';
import { FiCheck, FiX, FiEdit2, FiGitBranch } from 'react-icons/fi';

interface SuggestionInboxProps {
  personaId: string;
  compact?: boolean;
  onAccepted?: (suggestion: Suggestion) => void;
}

const SuggestionInbox: React.FC<SuggestionInboxProps> = ({ personaId, compact = false, onAccepted }) => {
  const { darkMode } = useTheme();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPayload, setEditPayload] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!personaId) return;
    setLoading(true);
    suggestionService.getForPersona(personaId)
      .then(setSuggestions)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [personaId]);

  const handleAccept = async (id: string) => {
    await suggestionService.accept(id);
    const accepted = suggestions.find(s => s.id === id);
    setSuggestions(prev => prev.filter(s => s.id !== id));
    if (accepted && onAccepted) onAccepted(accepted);
  };

  const handleDismiss = async (id: string) => {
    await suggestionService.dismiss(id);
    setSuggestions(prev => prev.filter(s => s.id !== id));
  };

  const handleEditAccept = async (id: string) => {
    await suggestionService.accept(id);
    const accepted = suggestions.find(s => s.id === id);
    setSuggestions(prev => prev.filter(s => s.id !== id));
    if (accepted && onAccepted) onAccepted({ ...accepted!, payload: { ...accepted!.payload, ...editPayload } });
    setEditingId(null);
    setEditPayload({});
  };

  const handleBulkAccept = async () => {
    const ids = suggestions.map(s => s.id);
    await suggestionService.bulkAction(ids, 'accept');
    suggestions.forEach(s => onAccepted?.(s));
    setSuggestions([]);
  };

  if (loading) return null;
  if (suggestions.length === 0) return null;

  return (
    <div className={`rounded-lg border p-3 mb-4 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className={`text-sm font-semibold flex items-center gap-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          <FiGitBranch className="w-4 h-4 text-purple-400" />
          Suggested Probes ({suggestions.length})
        </h4>
        {suggestions.length > 1 && (
          <button
            onClick={handleBulkAccept}
            className="text-xs text-purple-400 hover:text-purple-300"
          >
            Accept All
          </button>
        )}
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {suggestions.map(s => (
          <div
            key={s.id}
            className={`flex flex-wrap items-start gap-2 p-2 rounded text-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          >
            <div className="flex-1 min-w-0">
              <p className={`font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {s.payload.label || 'Suggested probe'}
              </p>
              {!compact && (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  From: {s.source_context.phase_name || s.source_context.workflow_id || 'workflow'}
                </p>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                onClick={() => handleAccept(s.id)}
                className="p-1 rounded hover:bg-green-600/20 text-green-400"
                title="Accept"
              >
                <FiCheck className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setEditingId(s.id); setEditPayload(s.payload); }}
                className="p-1 rounded hover:bg-blue-600/20 text-blue-400"
                title="Edit & Accept"
              >
                <FiEdit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDismiss(s.id)}
                className="p-1 rounded hover:bg-red-600/20 text-red-400"
                title="Dismiss"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
            {editingId === s.id && (
              <div className={`w-full mt-2 p-2 rounded border ${darkMode ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                <input
                  className={`w-full mb-1 px-2 py-1 text-xs rounded ${darkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}
                  value={editPayload.label || ''}
                  onChange={e => setEditPayload(p => ({ ...p, label: e.target.value }))}
                  placeholder="Probe label"
                />
                <textarea
                  className={`w-full mb-1 px-2 py-1 text-xs rounded resize-none ${darkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}
                  rows={2}
                  value={editPayload.question || ''}
                  onChange={e => setEditPayload(p => ({ ...p, question: e.target.value }))}
                  placeholder="Probe question"
                />
                <div className="flex gap-1 justify-end">
                  <button onClick={() => setEditingId(null)} className="text-xs text-gray-400 hover:text-gray-300 px-2 py-1">Cancel</button>
                  <button onClick={() => handleEditAccept(s.id)} className="text-xs text-green-400 hover:text-green-300 px-2 py-1">Save & Accept</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuggestionInbox;
